import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import PhygitalIcon from "@/components/PhygitalIcon";
import {
  Headphones, Users, Smartphone, ArrowRight, 
  ExternalLink, Copy, ChevronRight,
  ListMusic, BarChart3, Heart, Sparkles
} from "lucide-react";
import { toast } from "sonner";

// Tap In logo - stored locally for sync with admin
import { TAP_IN_LOGO } from "@/lib/integrationConfig";

export default function TapInPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("overview");

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  return (
    <div className="min-h-screen bg-white" data-testid="tap-in-page">
      {/* Header */}
      <header className="border-b border-gray-100 bg-white sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <PhygitalIcon className="w-8 h-8" />
            <span className="font-bold text-gray-900">Phygital</span>
          </Link>
          <Badge className="bg-blue-50 text-blue-600 border-blue-200">
            Partner Integration
          </Badge>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-12">
        {/* Hero Section - Clean White Design */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center mb-8">
            <img 
              src={TAP_IN_LOGO} 
              alt="Tap In" 
              className="w-32 h-32 rounded-3xl shadow-xl object-cover"
              data-testid="tap-in-logo"
            />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Tap In × Phygital
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            The music platform for curated event experiences. 
            Connect your playlist, tap in a song, and vibe with the crowd.
          </p>
        </div>

        {/* Main Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-8">
          <TabsList className="bg-gray-100 border border-gray-200 p-1 mx-auto flex w-fit">
            <TabsTrigger value="overview" className="data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow-sm">
              Overview
            </TabsTrigger>
            <TabsTrigger value="user-flow" className="data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow-sm">
              User Flow
            </TabsTrigger>
            <TabsTrigger value="integration" className="data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow-sm">
              API Integration
            </TabsTrigger>
            <TabsTrigger value="setup" className="data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow-sm">
              Setup Guide
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* What is Tap In */}
              <Card className="bg-white border-gray-200 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-gray-900 flex items-center gap-2">
                    <Headphones className="w-5 h-5 text-blue-500" />
                    What is Tap In?
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-gray-600 space-y-4">
                  <p>
                    Tap In is a music platform that connects event attendees with curated playlists. 
                    When guests RSVP to a Phygital event, they can "tap in a song" - their personal track 
                    is added to the event playlist.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="outline" className="border-blue-200 text-blue-600 bg-blue-50">Events</Badge>
                    <Badge variant="outline" className="border-purple-200 text-purple-600 bg-purple-50">Venues</Badge>
                    <Badge variant="outline" className="border-pink-200 text-pink-600 bg-pink-50">DJs</Badge>
                  </div>
                </CardContent>
              </Card>

              {/* Key Features */}
              <Card className="bg-white border-gray-200 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-gray-900 flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-amber-500" />
                    Key Features
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {[
                      { icon: ListMusic, text: "Private event playlists", color: "text-blue-500" },
                      { icon: Users, text: "Guest song requests", color: "text-purple-500" },
                      { icon: BarChart3, text: "DJ insights & analytics", color: "text-green-500" },
                      { icon: Smartphone, text: "Apple Music sync", color: "text-pink-500" },
                      { icon: Heart, text: "VIP auto-bypass", color: "text-amber-500" }
                    ].map((item, i) => (
                      <li key={i} className="flex items-center gap-3 text-gray-600">
                        <item.icon className={`w-5 h-5 ${item.color}`} />
                        {item.text}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              {/* First Pilot - HTHF */}
              <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200 md:col-span-2">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div className="flex items-center gap-4">
                      <img 
                        src={TAP_IN_LOGO} 
                        alt="Tap In" 
                        className="w-12 h-12 rounded-xl object-cover"
                      />
                      <div>
                        <Badge className="bg-green-100 text-green-700 border-green-200 mb-2">Live Pilot</Badge>
                        <h3 className="text-2xl font-bold text-gray-900">High Tech High Fashion (HTHF)</h3>
                        <p className="text-gray-600">LA Fashion Week 2026 • First Tap In Integration</p>
                      </div>
                    </div>
                    <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                      <ExternalLink className="w-4 h-4 mr-2" />
                      View Event
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* User Flow Tab */}
          <TabsContent value="user-flow">
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-gray-900">How It Works</CardTitle>
                <CardDescription className="text-gray-500">
                  The complete user journey from RSVP to event day
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-8">
                  {/* Phygital Flow */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <PhygitalIcon className="w-5 h-5" />
                      On Phygital
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      {[
                        { step: 1, title: "RSVP", desc: "Guest RSVPs to event and selects 'Tap In a Song' option" },
                        { step: 2, title: "Song Request", desc: "Guest submits their song choice to add to the playlist" },
                        { step: 3, title: "Confirmation", desc: "Tap In card appears with private playlist link" },
                        { step: 4, title: "Dashboard", desc: "Host sees DJ insights and manages playlist" }
                      ].map((item) => (
                        <div key={item.step} className="relative">
                          <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 h-full">
                            <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold mb-3">
                              {item.step}
                            </div>
                            <h5 className="font-semibold text-gray-900 mb-1">{item.title}</h5>
                            <p className="text-sm text-gray-600">{item.desc}</p>
                          </div>
                          {item.step < 4 && (
                            <ChevronRight className="hidden md:block absolute -right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-300" />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Tap In Flow */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <img src={TAP_IN_LOGO} alt="Tap In" className="w-5 h-5 rounded object-cover" />
                      On Tap In
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {[
                        { step: 1, title: "Sign Up", desc: "Pre-filled phone number from Phygital, pick username, sync Apple Music" },
                        { step: 2, title: "Access", desc: "VIP guests auto-bypass, others get notified when approved" },
                        { step: 3, title: "Enjoy", desc: "Access private playlist, add to 'My Playlists', see brand tags" }
                      ].map((item) => (
                        <div key={item.step} className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                          <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold mb-3">
                            {item.step}
                          </div>
                          <h5 className="font-semibold text-gray-900 mb-1">{item.title}</h5>
                          <p className="text-sm text-gray-600">{item.desc}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* API Integration Tab */}
          <TabsContent value="integration">
            <div className="space-y-6">
              {/* Data Flow */}
              <Card className="bg-white border-gray-200 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-gray-900">Data Flow</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {/* Phygital → Tap In */}
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <ArrowRight className="w-4 h-4 text-green-500" />
                        Phygital → Tap In
                      </h4>
                      <div className="space-y-2">
                        {[
                          { data: "Name", purpose: "Pre-fill Tap In account" },
                          { data: "Phone Number", purpose: "Pre-fill Tap In account" },
                          { data: "Location", purpose: "Pre-fill Tap In account" },
                          { data: "VIP Tag", purpose: "Auto bypass approval" },
                          { data: "RSVP Status", purpose: "Grant/revoke playlist access" }
                        ].map((item, i) => (
                          <div key={i} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2 border border-gray-100">
                            <span className="text-gray-900 font-medium">{item.data}</span>
                            <span className="text-gray-500 text-sm">{item.purpose}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Tap In → Phygital */}
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <ArrowRight className="w-4 h-4 text-blue-500 rotate-180" />
                        Tap In → Phygital
                      </h4>
                      <div className="space-y-2">
                        {[
                          { data: "Playlist Link", status: "active" },
                          { data: "DJ Insights Link", status: "active" },
                          { data: "Playlist Preview", status: "future" },
                          { data: "Playlist Title", status: "future" },
                          { data: "Cover Image", status: "future" }
                        ].map((item, i) => (
                          <div key={i} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2 border border-gray-100">
                            <span className="text-gray-900 font-medium">{item.data}</span>
                            <Badge variant={item.status === "active" ? "default" : "secondary"} className={item.status === "active" ? "bg-green-100 text-green-700 border-green-200" : "bg-gray-100 text-gray-600"}>
                              {item.status === "active" ? "✓ Active" : "Coming Soon"}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* API Endpoints */}
              <Card className="bg-white border-gray-200 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-gray-900">API Endpoints</CardTitle>
                  <CardDescription className="text-gray-500">
                    Technical documentation for Tap In integration
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {/* Webhook Endpoint */}
                    <div className="bg-gray-900 rounded-lg p-4 font-mono">
                      <div className="flex items-center justify-between mb-2">
                        <Badge className="bg-green-500 text-white">POST</Badge>
                        <Button variant="ghost" size="sm" className="text-gray-400 hover:text-white" onClick={() => copyToClipboard("/api/integrations/tap-in/webhook")}>
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                      <code className="text-green-400">/api/integrations/tap-in/webhook</code>
                      <p className="text-gray-400 text-sm mt-2">Receives playlist updates and DJ insight data from Tap In</p>
                    </div>

                    {/* RSVP Data Endpoint */}
                    <div className="bg-gray-900 rounded-lg p-4 font-mono">
                      <div className="flex items-center justify-between mb-2">
                        <Badge className="bg-blue-500 text-white">GET</Badge>
                        <Button variant="ghost" size="sm" className="text-gray-400 hover:text-white" onClick={() => copyToClipboard("/api/integrations/tap-in/rsvp/{event_id}/{user_id}")}>
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                      <code className="text-blue-400">/api/integrations/tap-in/rsvp/{'{event_id}'}/{'{user_id}'}</code>
                      <p className="text-gray-400 text-sm mt-2">Returns user RSVP data for pre-fill and VIP status</p>
                    </div>

                    {/* Sample Payload */}
                    <div className="bg-gray-900 rounded-lg p-4">
                      <h5 className="text-white font-semibold mb-2">Sample Payload (on RSVP)</h5>
                      <pre className="text-sm text-gray-300 overflow-x-auto">
{`{
  "event_id": "evt_xxx",
  "event_name": "High Tech High Fashion",
  "user": {
    "name": "Guest Name",
    "phone": "+1234567890",
    "location": "Los Angeles, CA"
  },
  "is_vip": true,
  "rsvp_status": "confirmed"
}`}
                      </pre>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Setup Guide Tab */}
          <TabsContent value="setup">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Tap In Setup */}
              <Card className="bg-blue-50 border-blue-200">
                <CardHeader>
                  <CardTitle className="text-gray-900 flex items-center gap-2">
                    <img src={TAP_IN_LOGO} alt="Tap In" className="w-5 h-5 rounded object-cover" />
                    Tap In Setup
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {[
                      "Sign up from event playlist link",
                      "Configure playlist popup access",
                      "Set up private playlist approval flow",
                      "Create shareable playlist link",
                      "Add brand tag (Phygital)",
                      "Add co-hosts with brand tags",
                      "Generate DJ insight share link",
                      "Configure API integration"
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-3 text-gray-700">
                        <div className="w-5 h-5 rounded-full border border-blue-300 bg-white flex items-center justify-center mt-0.5 flex-shrink-0">
                          <span className="text-xs text-blue-600 font-medium">{i + 1}</span>
                        </div>
                        {item}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              {/* Phygital Setup */}
              <Card className="bg-purple-50 border-purple-200">
                <CardHeader>
                  <CardTitle className="text-gray-900 flex items-center gap-2">
                    <PhygitalIcon className="w-5 h-5" />
                    Phygital Setup
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {[
                      "Enable Tap In in event settings",
                      "Add playlist link to event",
                      "Configure RSVP experience options",
                      "Set up data pre-fill for Tap In",
                      "Configure webhook receiver",
                      "Add DJ insights to host dashboard",
                      "Test VIP auto-bypass flow",
                      "Review analytics integration"
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-3 text-gray-700">
                        <div className="w-5 h-5 rounded-full border border-purple-300 bg-white flex items-center justify-center mt-0.5 flex-shrink-0">
                          <span className="text-xs text-purple-600 font-medium">{i + 1}</span>
                        </div>
                        {item}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              {/* Contact */}
              <Card className="bg-white border-gray-200 md:col-span-2">
                <CardContent className="pt-6">
                  <div className="text-center">
                    <img src={TAP_IN_LOGO} alt="Tap In" className="w-16 h-16 rounded-2xl mx-auto mb-4 shadow-lg object-cover" />
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Ready to Integrate?</h3>
                    <p className="text-gray-600 mb-4">
                      Contact the Phygital team to get started with Tap In integration for your event.
                    </p>
                    <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                      <Heart className="w-4 h-4 mr-2" />
                      Get Started
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-100 mt-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 py-8 text-center text-gray-500 text-sm flex items-center justify-center gap-2">
          <img src={TAP_IN_LOGO} alt="Tap In" className="w-5 h-5 rounded object-cover" />
          <p>Tap In × Phygital Partnership • First Pilot: HTHF LA Fashion Week 2026</p>
        </div>
      </footer>
    </div>
  );
}
